/**
 * Faturas mensais do corporativo (Adendo C) — Fase 5.
 *
 * ---------------------------------------------------------------------------
 * LISTA E PAINEL NA MESMA PÁGINA
 * ---------------------------------------------------------------------------
 * A fatura selecionada abre como painel no lugar da lista (com o caminho de
 * volta), no espírito de `/frota/$veiculoId`: o detalhe não reaproveita nada da
 * listagem. Não há rota filha porque o financeiro trabalha a competência em
 * sequência — abre, resolve, volta — e o estado local preserva filtros e página.
 *
 * NÃO EXISTE "NOVA FATURA": ela nasce da acumulação das reservas fechadas do
 * cliente FATURADO_MENSAL. O que se faz aqui é fechar, dar baixa, cancelar
 * (com motivo obrigatório) e anexar a nota fiscal emitida fora.
 */

import { ROTULO_DO_STATUS_DE_FATURA, STATUS_DE_FATURA, type StatusDeFatura } from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  CircleCheck,
  CircleSlash,
  Clock,
  FileText,
  FolderOpen,
  LoaderCircle,
  type LucideIcon,
  Paperclip,
  ReceiptText,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useUrlDeLeitura } from '@/features/arquivos/url-de-leitura'
import { nomeDaConta, useContas } from '@/features/contas/api'
import {
  rotuloDaCompetencia,
  useCancelarFatura,
  useFatura,
  useFaturas,
  useFecharFatura,
  useMarcarFaturaPaga,
  type Fatura,
  type FaturaDetalhada,
  type NotaFiscal,
} from '@/features/faturas/api'
import { DialogoDeNotaFiscal } from '@/features/faturas/dialogo-de-nota-fiscal'
import { referenciaDaReserva } from '@/features/multas/api'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR, formatarData, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'

const TAMANHO_DA_PAGINA = 20

// ---------------------------------------------------------------------------
// Badge de status — cor + ícone + rótulo, nunca só cor
// ---------------------------------------------------------------------------

const APRESENTACAO_DO_STATUS = {
  ABERTA: { icone: FolderOpen, variante: 'info' },
  FECHADA: { icone: Clock, variante: 'warning' },
  PAGA: { icone: CircleCheck, variante: 'success' },
  VENCIDA: { icone: TriangleAlert, variante: 'danger' },
  CANCELADA: { icone: CircleSlash, variante: 'neutral' },
} as const satisfies Readonly<
  Record<StatusDeFatura, { readonly icone: LucideIcon; readonly variante: BadgeProps['variant'] }>
>

function BadgeDeStatusDeFatura({ status }: { readonly status: StatusDeFatura }): ReactElement {
  const { icone: Icone, variante } = APRESENTACAO_DO_STATUS[status]
  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_FATURA[status]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// A página
// ---------------------------------------------------------------------------

export function FaturasPage(): ReactElement {
  const [pagina, setPagina] = useState(1)
  const [status, setStatus] = useState<StatusDeFatura | ''>('')
  const [accountId, setAccountId] = useState('')
  const [faturaAberta, setFaturaAberta] = useState<string | null>(null)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, status, accountId }),
    [pagina, status, accountId],
  )
  const consulta = useFaturas(filtros)

  // As contas para o filtro e para dar NOME às linhas — a fatura só traz o id.
  // Se a leitura falhar, a linha degrada para a referência curta do id.
  const contas = useContas({
    pagina: 1,
    tamanho: 100,
    busca: '',
    tipo: '',
    status: '',
    regimeDeFaturamento: '',
  })
  const nomePorConta = useMemo(() => {
    const mapa = new Map<string, string>()
    for (const conta of contas.data?.itens ?? []) {
      mapa.set(conta.id, nomeDaConta(conta))
    }
    return mapa
  }, [contas.data])

  const temFiltro = status !== '' || accountId !== ''

  function limparFiltros(): void {
    setStatus('')
    setAccountId('')
    setPagina(1)
  }

  const colunas = useMemo<ColumnDef<Fatura>[]>(
    () => [
      {
        id: 'conta',
        header: 'Conta',
        accessorFn: (linha: Fatura) => nomePorConta.get(linha.accountId) ?? linha.accountId,
        cell: ({ row }) => {
          const fatura: Fatura = row.original
          const nome = nomePorConta.get(fatura.accountId)
          return (
            <p className="min-w-0 truncate font-medium text-foreground" title={fatura.accountId}>
              {nome ?? `#${fatura.accountId.slice(0, 8)}`}
            </p>
          )
        },
      },
      {
        id: 'competencia',
        header: 'Competência',
        accessorFn: (linha: Fatura) => `${String(linha.ano)}-${String(linha.mes).padStart(2, '0')}`,
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-money text-foreground">
            {rotuloDaCompetencia(row.original.mes, row.original.ano)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeStatusDeFatura status={row.original.status} />,
      },
      {
        id: 'total',
        header: 'Total',
        meta: { alinhamento: 'direita' },
        // Wire de centavos: ordenar a string mentiria ("900" > "10000").
        enableSorting: false,
        cell: ({ row }) => (
          <span className="money-cell block min-w-24 text-foreground">
            {formatarMoedaDoWire(row.original.total)}
          </span>
        ),
      },
      {
        id: 'vencimento',
        header: 'Vencimento',
        accessorFn: (linha: Fatura) => linha.venceEm ?? '',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
            {row.original.venceEm === null ? SEM_VALOR : formatarData(row.original.venceEm)}
          </span>
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFaturaAberta(row.original.id)
            }}
          >
            Abrir
          </Button>
        ),
      },
    ],
    [nomePorConta],
  )

  if (faturaAberta !== null) {
    return (
      <DetalheDaFatura
        id={faturaAberta}
        nomeDaContaPorId={nomePorConta}
        aoVoltar={() => {
          setFaturaAberta(null)
        }}
      />
    )
  }

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Faturas</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          A fatura mensal do corporativo nasce sozinha, das reservas fechadas do cliente faturado.
          Aqui o financeiro fecha a competência, dá baixa no pagamento e anexa a nota fiscal emitida
          fora.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-faturas">Status</Label>
          <Select
            id="filtro-status-faturas"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_FATURA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {STATUS_DE_FATURA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS_DE_FATURA[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-conta-faturas">Conta</Label>
          <Select
            id="filtro-conta-faturas"
            value={accountId}
            onChange={(evento) => {
              setPagina(1)
              setAccountId(evento.target.value)
            }}
          >
            <option value="">Todas</option>
            {(contas.data?.itens ?? []).map((conta) => (
              <option key={conta.id} value={conta.id}>
                {nomeDaConta(conta)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Faturas mensais"
        estadoVazio={
          <EmptyState
            icone={ReceiptText}
            titulo={temFiltro ? 'Nenhuma fatura com esses filtros' : 'Nenhuma fatura ainda'}
            descricao={
              temFiltro
                ? 'Ajuste o status ou a conta para ver outros resultados.'
                : 'A primeira fatura aparece quando uma reserva de cliente FATURADO MENSAL fechar — a acumulação é automática, não existe criação manual.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// O painel de detalhe
// ---------------------------------------------------------------------------

function DetalheDaFatura({
  id,
  nomeDaContaPorId,
  aoVoltar,
}: {
  readonly id: string
  readonly nomeDaContaPorId: ReadonlyMap<string, string>
  readonly aoVoltar: () => void
}): ReactElement {
  const consulta = useFatura(id)

  if (consulta.isPending) {
    return (
      <div className="mx-auto w-full max-w-6xl">
        <LoadingState rotulo="Carregando a fatura…" />
      </div>
    )
  }
  if (consulta.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <Button variant="ghost" onClick={aoVoltar}>
          <ArrowLeft aria-hidden="true" /> Faturas
        </Button>
        <ErrorState
          titulo="Não foi possível carregar a fatura"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      </div>
    )
  }

  const fatura = consulta.data
  const nome = nomeDaContaPorId.get(fatura.accountId) ?? `#${fatura.accountId.slice(0, 8)}`

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="space-y-1">
        <button
          type="button"
          onClick={aoVoltar}
          className="inline-flex items-center gap-1 rounded-md text-sm text-fg-secondary
            hover:text-foreground focus-visible:outline-none focus-visible:ring-2
            focus-visible:ring-ring"
        >
          <ArrowLeft className="size-4" aria-hidden="true" /> Faturas
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold">
            Fatura {rotuloDaCompetencia(fatura.mes, fatura.ano)}
          </h1>
          <BadgeDeStatusDeFatura status={fatura.status} />
        </div>
        <p className="text-sm text-fg-secondary">{nome}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CartaoDeItens fatura={fatura} />
          <CartaoDeNotas fatura={fatura} />
        </div>
        <div className="space-y-6">
          <CartaoDeResumo fatura={fatura} />
        </div>
      </div>
    </div>
  )
}

function CartaoDeResumo({ fatura }: { readonly fatura: FaturaDetalhada }): ReactElement {
  const podeAtualizar = usePermissao('invoice.update')

  const fechar = useFecharFatura()
  const marcarPaga = useMarcarFaturaPaga()
  const cancelar = useCancelarFatura()

  const [cancelando, setCancelando] = useState(false)
  const [motivo, setMotivo] = useState('')

  const podeFechar = podeAtualizar && fatura.status === 'ABERTA'
  const podeDarBaixa = podeAtualizar && (fatura.status === 'FECHADA' || fatura.status === 'VENCIDA')
  const podeCancelar = podeAtualizar && (fatura.status === 'ABERTA' || fatura.status === 'FECHADA')

  const linhas: readonly (readonly [string, string])[] = [
    ['Total', formatarMoedaDoWire(fatura.total)],
    ['Fechada em', fatura.fechadaEm === null ? SEM_VALOR : formatarDataHora(fatura.fechadaEm)],
    ['Vence em', fatura.venceEm === null ? SEM_VALOR : formatarData(fatura.venceEm)],
    ['Paga em', fatura.pagaEm === null ? SEM_VALOR : formatarDataHora(fatura.pagaEm)],
    [
      'Cancelada em',
      fatura.canceladaEm === null ? SEM_VALOR : formatarDataHora(fatura.canceladaEm),
    ],
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="space-y-2">
          {linhas.map(([rotulo, valor]) => (
            <div key={rotulo} className="flex items-baseline justify-between gap-4">
              <dt className="text-sm text-fg-secondary">{rotulo}</dt>
              <dd className="text-sm font-medium tabular-nums">{valor}</dd>
            </div>
          ))}
        </dl>

        {fatura.observacoes === null ? null : (
          <p className="rounded-md border border-border bg-elevated p-2 text-xs text-fg-secondary">
            {fatura.observacoes}
          </p>
        )}

        {fatura.transacaoIds.length > 0 ? (
          <p className="text-xs text-fg-muted">
            {fatura.transacaoIds.length === 1
              ? '1 transação de pagamento vinculada'
              : `${String(fatura.transacaoIds.length)} transações de pagamento vinculadas`}{' '}
            —{' '}
            <Link
              to="/pagamentos"
              className="text-brand-700 underline-offset-4 hover:underline dark:text-brand-500"
            >
              ver em Pagamentos →
            </Link>
          </p>
        ) : null}

        {podeFechar || podeDarBaixa || podeCancelar ? (
          <div className="flex flex-wrap gap-2">
            {podeFechar ? (
              <Button
                disabled={fechar.isPending}
                onClick={() => {
                  fechar.mutate(
                    { id: fatura.id },
                    {
                      onSuccess: () => {
                        toast.success('Fatura fechada — o total travou e o vencimento correu.')
                      },
                      onError: (erro) => {
                        toast.error(errorMessage(erro))
                      },
                    },
                  )
                }}
              >
                {fechar.isPending ? <LoaderCircle className="animate-spin" /> : null}
                Fechar agora
              </Button>
            ) : null}
            {podeDarBaixa ? (
              <Button
                disabled={marcarPaga.isPending}
                onClick={() => {
                  marcarPaga.mutate(
                    { id: fatura.id },
                    {
                      onSuccess: () => {
                        toast.success('Baixa registrada — fatura paga.')
                      },
                      onError: (erro) => {
                        toast.error(errorMessage(erro))
                      },
                    },
                  )
                }}
              >
                {marcarPaga.isPending ? <LoaderCircle className="animate-spin" /> : null}
                Marcar paga
              </Button>
            ) : null}
            {podeCancelar ? (
              <Button
                variant="destructive"
                onClick={() => {
                  setCancelando(true)
                }}
              >
                Cancelar…
              </Button>
            ) : null}
          </div>
        ) : null}

        {podeDarBaixa ? (
          <p className="text-xs text-fg-muted">
            O corporativo paga por TED/boleto fora do provedor — quem confere o extrato dá a baixa
            aqui.
          </p>
        ) : null}
      </CardContent>

      <Dialog
        open={cancelando}
        onOpenChange={(abrir) => {
          if (!abrir) {
            setCancelando(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar a fatura</DialogTitle>
            <DialogDescription>
              O motivo é obrigatório e vai para as observações e para a trilha de auditoria. Fatura
              PAGA não cancela — dinheiro que já andou volta por estorno, em Pagamentos.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={motivo}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            placeholder="Por que esta fatura sai de circulação? (mínimo de 5 caracteres)"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setCancelando(false)
              }}
            >
              Voltar
            </Button>
            <Button
              variant="destructive"
              disabled={motivo.trim().length < 5 || cancelar.isPending}
              onClick={() => {
                cancelar.mutate(
                  { id: fatura.id, motivo: motivo.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Fatura cancelada, com o motivo na trilha.')
                      setCancelando(false)
                      setMotivo('')
                    },
                    onError: (erro) => {
                      toast.error(errorMessage(erro))
                    },
                  },
                )
              }}
            >
              {cancelar.isPending ? <LoaderCircle className="animate-spin" /> : null}
              Confirmar cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function CartaoDeItens({ fatura }: { readonly fatura: FaturaDetalhada }): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ReceiptText className="size-4" /> Itens da competência
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {fatura.itens.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            Nenhum item ainda — as reservas fechadas do mês entram aqui sozinhas.
          </p>
        ) : (
          <>
            {fatura.itens.map((item) => (
              <div
                key={item.id}
                className="flex items-baseline justify-between gap-4 rounded-md border
                  border-border px-3 py-2 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate text-foreground">{item.descricao}</span>
                  <span className="block text-xs text-fg-muted">
                    {item.reservationId === null ? (
                      'ajuste manual'
                    ) : (
                      <Link
                        to="/reservas/$reservaId"
                        params={{ reservaId: item.reservationId }}
                        className="hover:underline"
                        title={item.reservationId}
                      >
                        reserva {referenciaDaReserva(item.reservationId)}
                      </Link>
                    )}{' '}
                    · {formatarData(item.criadoEm)}
                  </span>
                </span>
                <span className="money-cell shrink-0 text-foreground">
                  {formatarMoedaDoWire(item.valor)}
                </span>
              </div>
            ))}
            <Separator />
            <div className="flex items-baseline justify-between gap-4 px-3 font-semibold">
              <span>Total</span>
              <span className="money-cell">{formatarMoedaDoWire(fatura.total)}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CartaoDeNotas({ fatura }: { readonly fatura: FaturaDetalhada }): ReactElement {
  const podeAnexar = usePermissao('invoice.create')
  const [anexando, setAnexando] = useState(false)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="size-4" /> Notas fiscais anexadas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {fatura.notasFiscais.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            Nenhuma nota anexada. A v1 não emite — o XML sai do sistema emissor e é vinculado aqui.
          </p>
        ) : (
          fatura.notasFiscais.map((nota) => <LinhaDeNota key={nota.id} nota={nota} />)
        )}

        {podeAnexar ? (
          <Button
            variant="outline"
            onClick={() => {
              setAnexando(true)
            }}
          >
            <Paperclip aria-hidden="true" />
            Anexar nota fiscal
          </Button>
        ) : null}
      </CardContent>

      {anexando ? (
        <DialogoDeNotaFiscal
          alvo={{ invoiceId: fatura.id }}
          valorEsperado={fatura.total}
          aoFechar={() => {
            setAnexando(false)
          }}
        />
      ) : null}
    </Card>
  )
}

function LinhaDeNota({ nota }: { readonly nota: NotaFiscal }): ReactElement {
  const leitura = useUrlDeLeitura(nota.arquivoChave)

  return (
    <div className="space-y-1 rounded-md border border-border px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="break-all font-medium tabular-money text-foreground">
          {nota.chaveDeAcesso}
        </span>
        <span className="money-cell shrink-0">{formatarMoedaDoWire(nota.valor)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>anexada em {formatarDataHora(nota.criadoEm)}</span>
        {leitura.data === undefined ? null : (
          <a
            href={leitura.data.urlDeLeitura}
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-700 underline-offset-4 hover:underline dark:text-brand-500"
          >
            Ver XML ↗
          </a>
        )}
      </div>
    </div>
  )
}
